import React, {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';

const AdminReports = () => {

    const [reports, setReports] = useState([]);

    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [status, setStatus] = useState('');
    const [type, setType] = useState('');

    const [actionLoadingId, setActionLoadingId] =
        useState(null);

    const [moderationModalOpen, setModerationModalOpen] =
        useState(false);

    const [moderationReport, setModerationReport] =
        useState(null);

    const [moderationAction, setModerationAction] =
        useState('');

    const [moderationNote, setModerationNote] =
        useState('');

    /*
     * CUSTOM ACTION DROPDOWN
     */
    const [openActionReportId, setOpenActionReportId] =
        useState(null);

    const [actionModalOpen, setActionModalOpen] = useState(false);
    const [actionReport, setActionReport] = useState(null);
    const [actionNote, setActionNote] = useState('');

    const dropdownRef = useRef(null);

    /*
     * EMAIL
     */
    const [emailModalOpen, setEmailModalOpen] =
        useState(false);

    const [emailReport, setEmailReport] =
        useState(null);

    const [emailBody, setEmailBody] =
        useState('');

    const [emailSending, setEmailSending] =
        useState(false);

    const [emailSuccess, setEmailSuccess] =
        useState('');

    /*
     * COMMUNICATION HISTORY
     */
    const [historyModalOpen, setHistoryModalOpen] =
        useState(false);

    const [historyReport, setHistoryReport] =
        useState(null);

    const [historyItems, setHistoryItems] =
        useState([]);

    const [historyLoading, setHistoryLoading] =
        useState(false);

    const size = 20;


    const openModerationModal = (report) => {
        setOpenActionReportId(null);
        setModerationReport(report);
        setModerationAction('');
        setModerationNote('');
        setModerationModalOpen(true);
    };

    const closeModerationModal = () => {
        if (actionLoadingId) return;

        setModerationModalOpen(false);
        setModerationReport(null);
        setModerationAction('');
        setModerationNote('');
    };





    /*
     * =============================
     * LOAD REPORTS
     * =============================
     */

    const loadReports = useCallback(
        async (
            requestedPage = 0,
            requestedStatus = '',
            requestedType = ''
        ) => {

            try {

                setLoading(true);
                setError('');

                const token =
                    localStorage.getItem('authToken');

                const params =
                    new URLSearchParams();

                params.set(
                    'page',
                    requestedPage
                );

                params.set(
                    'size',
                    size
                );

                if (requestedStatus) {
                    params.set(
                        'status',
                        requestedStatus
                    );
                }

                if (requestedType) {
                    params.set(
                        'type',
                        requestedType
                    );
                }


                const response =
                    await fetch(
                        `${process.env.REACT_APP_API_URL}/api/admin/reports?${params.toString()}`,
                        {
                            headers: {
                                Authorization:
                                    token?.startsWith(
                                        'Bearer '
                                    )
                                        ? token
                                        : `Bearer ${token}`
                            }
                        }
                    );


                if (!response.ok) {

                    throw new Error(
                        `Reports request failed: ${response.status}`
                    );
                }


                const data =
                    await response.json();


                setReports(
                    Array.isArray(data.content)
                        ? data.content
                        : []
                );

                setPage(
                    data.number || 0
                );

                setTotalPages(
                    data.totalPages || 0
                );


            } catch (error) {

                console.error(
                    '❌ Admin reports error:',
                    error
                );

                setError(
                    'Unable to load reports.'
                );

            } finally {

                setLoading(false);
            }
        },

        [size]
    );

    // const openActionModal = (report) => {
    //     setOpenActionReportId(null);

    //     setActionReport(report);
    //     setModerationAction('');
    //     setActionNote('');
    //     setActionModalOpen(true);
    // };

    const closeActionModal = () => {
        if (actionLoadingId) return;

        setActionModalOpen(false);
        setActionReport(null);
        setModerationAction('');
        setActionNote('');
    };


    /*
     * =============================
     * STATUS UPDATE
     * =============================
     */

    const updateReportStatus = async (
        reportId,
        action
    ) => {

        try {

            setActionLoadingId(reportId);
            setError('');
            setOpenActionReportId(null);

            const token =
                localStorage.getItem(
                    'authToken'
                );


            const response =
                await fetch(
                    `${process.env.REACT_APP_API_URL}/api/admin/reports/${reportId}/${action}`,
                    {
                        method: 'PATCH',

                        headers: {
                            'Content-Type':
                                'application/json',

                            Authorization:
                                token?.startsWith(
                                    'Bearer '
                                )
                                    ? token
                                    : `Bearer ${token}`
                        },

                        body: JSON.stringify({
                            adminNote: ''
                        })
                    }
                );


            if (!response.ok) {

                const data =
                    await response
                        .json()
                        .catch(() => ({}));

                throw new Error(
                    data.message ||
                    'Unable to update report.'
                );
            }


            await loadReports(
                page,
                status,
                type
            );


        } catch (error) {

            console.error(
                '❌ Report status error:',
                error
            );

            setError(
                error.message ||
                'Unable to update report.'
            );

        } finally {

            setActionLoadingId(null);
        }
    };


    /*
     * =============================
     * EMAIL
     * =============================
     */

    const openEmailModal = (
        report,
        recipientType
    ) => {

        setOpenActionReportId(null);

        setEmailReport({
            ...report,
            recipientType
        });

        setEmailBody('');
        setEmailSuccess('');
        setEmailModalOpen(true);
    };


    const closeEmailModal = () => {

        if (emailSending) return;

        setEmailModalOpen(false);
        setEmailReport(null);
        setEmailBody('');
        setEmailSuccess('');
    };


    const sendReportEmail = async () => {

        if (!emailReport?.id) {
            return;
        }

        if (!emailBody.trim()) {
            return;
        }


        try {

            setEmailSending(true);
            setEmailSuccess('');
            setError('');


            const token =
                localStorage.getItem(
                    'authToken'
                );


            const response =
                await fetch(
                    `${process.env.REACT_APP_API_URL}/api/admin/reports/${emailReport.id}/email`,
                    {
                        method: 'POST',

                        headers: {
                            'Content-Type':
                                'application/json',

                            Authorization:
                                token?.startsWith(
                                    'Bearer '
                                )
                                    ? token
                                    : `Bearer ${token}`
                        },

                        body: JSON.stringify({
                            recipientType:
                                emailReport.recipientType,

                            message:
                                emailBody.trim()
                        })
                    }
                );


            const data =
                await response
                    .json()
                    .catch(() => ({}));


            if (!response.ok) {

                throw new Error(
                    data.message ||
                    'Unable to send email.'
                );
            }


            setEmailSuccess(
                'Email sent successfully.'
            );

            setEmailBody('');


            window.setTimeout(() => {

                setEmailModalOpen(false);
                setEmailReport(null);
                setEmailSuccess('');

            }, 1200);


        } catch (error) {

            console.error(
                '❌ Email error:',
                error
            );

            setError(
                error.message ||
                'Unable to send email.'
            );

        } finally {

            setEmailSending(false);
        }
    };


    const confirmModerationAction = async () => {

        if (
            !moderationReport?.id ||
            !moderationAction
        ) {
            return;
        }

        try {

            setActionLoadingId(
                moderationReport.id
            );

            setError('');

            const token =
                localStorage.getItem(
                    'authToken'
                );

            const authHeader =
                token?.startsWith('Bearer ')
                    ? token
                    : `Bearer ${token}`;


            /*
             * 1. APPLY REAL USER ACTION
             */

            if (
                moderationAction === 'SUSPEND' &&
                moderationReport.reportedUserId
            ) {

                const suspendResponse =
                    await fetch(
                        `${process.env.REACT_APP_API_URL}/api/admin/users/${moderationReport.reportedUserId}/suspend`,
                        {
                            method: 'PATCH',

                            headers: {
                                Authorization:
                                    authHeader
                            }
                        }
                    );


                if (!suspendResponse.ok) {

                    const data =
                        await suspendResponse
                            .json()
                            .catch(() => ({}));

                    throw new Error(
                        data.message ||
                        'Unable to suspend user.'
                    );
                }
            }


            if (
                moderationAction === 'BAN' &&
                moderationReport.reportedUserId
            ) {

                const banResponse =
                    await fetch(
                        `${process.env.REACT_APP_API_URL}/api/admin/users/${moderationReport.reportedUserId}/ban`,
                        {
                            method: 'PATCH',

                            headers: {
                                Authorization:
                                    authHeader
                            }
                        }
                    );


                if (!banResponse.ok) {

                    const data =
                        await banResponse
                            .json()
                            .catch(() => ({}));

                    throw new Error(
                        data.message ||
                        'Unable to ban user.'
                    );
                }
            }


            /*
             * 2. SAVE REPORT AS ACTION_TAKEN
             */

            const adminNote = [
                `Action: ${moderationAction}`,

                moderationNote.trim()
                    ? `Note: ${moderationNote.trim()}`
                    : ''
            ]
                .filter(Boolean)
                .join('\n');


            const reportResponse =
                await fetch(
                    `${process.env.REACT_APP_API_URL}/api/admin/reports/${moderationReport.id}/action-taken`,
                    {
                        method: 'PATCH',

                        headers: {
                            'Content-Type':
                                'application/json',

                            Authorization:
                                authHeader
                        },

                        body: JSON.stringify({
                            adminNote
                        })
                    }
                );


            if (!reportResponse.ok) {

                const data =
                    await reportResponse
                        .json()
                        .catch(() => ({}));

                throw new Error(
                    data.message ||
                    'Unable to update report.'
                );
            }


            setModerationModalOpen(false);
            setModerationReport(null);
            setModerationAction('');
            setModerationNote('');


            await loadReports(
                page,
                status,
                type
            );


        } catch (error) {

            console.error(
                '❌ Moderation action error:',
                error
            );

            setError(
                error.message ||
                'Unable to apply moderation action.'
            );

        } finally {

            setActionLoadingId(null);
        }
    };


    /*
     * =============================
     * COMMUNICATION HISTORY
     * =============================
     */

    const openHistoryModal =
        async (report) => {

            try {

                setOpenActionReportId(null);

                setHistoryReport(report);
                setHistoryItems([]);
                setHistoryModalOpen(true);
                setHistoryLoading(true);


                const token =
                    localStorage.getItem(
                        'authToken'
                    );


                const response =
                    await fetch(
                        `${process.env.REACT_APP_API_URL}/api/admin/reports/${report.id}/communications`,
                        {
                            headers: {
                                Authorization:
                                    token?.startsWith(
                                        'Bearer '
                                    )
                                        ? token
                                        : `Bearer ${token}`
                            }
                        }
                    );


                if (!response.ok) {

                    throw new Error(
                        'Unable to load communication history.'
                    );
                }


                const data =
                    await response.json();


                setHistoryItems(
                    Array.isArray(data)
                        ? data
                        : []
                );


            } catch (error) {

                console.error(
                    '❌ Communication history error:',
                    error
                );

                setError(
                    error.message ||
                    'Unable to load communication history.'
                );

            } finally {

                setHistoryLoading(false);
            }
        };


    const closeHistoryModal = () => {

        setHistoryModalOpen(false);
        setHistoryReport(null);
        setHistoryItems([]);
    };


    /*
     * =============================
     * FILTERS
     * =============================
     */

    const applyFilters = () => {

        loadReports(
            0,
            status,
            type
        );
    };


    const clearFilters = () => {

        setStatus('');
        setType('');

        loadReports(
            0,
            '',
            ''
        );
    };


    /*
     * =============================
     * CLOSE DROPDOWN OUTSIDE
     * =============================
     */

    useEffect(() => {

        const handleOutsideClick =
            (event) => {

                if (
                    dropdownRef.current &&
                    !dropdownRef.current.contains(
                        event.target
                    )
                ) {
                    setOpenActionReportId(
                        null
                    );
                }
            };


        document.addEventListener(
            'mousedown',
            handleOutsideClick
        );


        return () => {

            document.removeEventListener(
                'mousedown',
                handleOutsideClick
            );
        };

    }, []);


    /*
     * ESCAPE CLOSE
     */

    useEffect(() => {

        const handleKeyDown =
            (event) => {

                if (
                    event.key !== 'Escape'
                ) {
                    return;
                }

                setOpenActionReportId(
                    null
                );

                if (!emailSending) {
                    setEmailModalOpen(false);
                }

                setHistoryModalOpen(false);
            };


        document.addEventListener(
            'keydown',
            handleKeyDown
        );


        return () =>
            document.removeEventListener(
                'keydown',
                handleKeyDown
            );

    }, [emailSending]);


    /*
     * SCROLL FIX
     */

    useEffect(() => {

        const html =
            document.documentElement;

        const body =
            document.body;

        const root =
            document.getElementById(
                'root'
            );


        const oldHtmlOverflow =
            html.style.overflow;

        const oldBodyOverflow =
            body.style.overflow;

        const oldRootOverflow =
            root?.style.overflow;


        html.style.overflowY =
            'auto';

        html.style.overflowX =
            'hidden';

        body.style.overflowY =
            'auto';

        body.style.overflowX =
            'hidden';

        body.style.height =
            'auto';

        body.style.minHeight =
            '100vh';


        if (root) {

            root.style.overflowY =
                'visible';

            root.style.overflowX =
                'hidden';

            root.style.height =
                'auto';

            root.style.minHeight =
                '100vh';
        }


        return () => {

            html.style.overflow =
                oldHtmlOverflow;

            body.style.overflow =
                oldBodyOverflow;

            if (root) {
                root.style.overflow =
                    oldRootOverflow || '';
            }
        };

    }, []);


    /*
     * INITIAL LOAD
     */

    useEffect(() => {

        loadReports(
            0,
            '',
            ''
        );

    }, [loadReports]);


    /*
     * =============================
     * STATUS HELPERS
     * =============================
     */

    const getStatusStyle =
        (reportStatus) => {

            switch (reportStatus) {

                case 'ACTION_TAKEN':

                    return {
                        background:
                            '#dcfce7',

                        color:
                            '#15803d'
                    };


                case 'DISMISSED':

                    return {
                        background:
                            '#fee2e2',

                        color:
                            '#b91c1c'
                    };


                case 'REVIEWED':

                    return {
                        background:
                            '#e0e7ff',

                        color:
                            '#4338ca'
                    };


                default:

                    return {
                        background:
                            '#fff7d6',

                        color:
                            '#b45309'
                    };
            }
        };


    /*
     * =============================
     * UI
     * =============================
     */

    return (

        <main
            style={{
                width: '100%',
                minHeight: '100vh',

                overflowX: 'hidden',

                background:
                    '#f8fafc',

                padding:
                    '30px'
            }}
        >

            <div
                style={{
                    width: '100%',
                    maxWidth: '1200px',
                    margin: '0 auto'
                }}
            >

                {/* HEADER */}

                <div
                    style={{
                        marginBottom:
                            '28px'
                    }}
                >

                    <h1
                        style={{
                            margin: 0,

                            fontSize:
                                '32px',

                            color:
                                '#0f172a'
                        }}
                    >
                        Reports
                    </h1>


                    <p
                        style={{
                            margin:
                                '8px 0 0',

                            color:
                                '#64748b'
                        }}
                    >
                        Review and manage user reports.
                    </p>

                </div>


                {/* FILTERS */}

                <div
                    style={{
                        display: 'flex',

                        gap: '12px',

                        flexWrap:
                            'wrap',

                        alignItems:
                            'center',

                        marginBottom:
                            '24px',

                        padding:
                            '18px',

                        border:
                            '1px solid #e2e8f0',

                        borderRadius:
                            '16px',

                        background:
                            '#fff'
                    }}
                >

                    <select
                        value={status}

                        onChange={
                            (event) =>
                                setStatus(
                                    event
                                        .target
                                        .value
                                )
                        }

                        style={{
                            padding:
                                '11px 14px',

                            border:
                                '1px solid #d1d5db',

                            borderRadius:
                                '10px',

                            background:
                                '#fff',

                            fontWeight:
                                '600'
                        }}
                    >

                        <option value="">
                            All statuses
                        </option>

                        <option value="PENDING">
                            Pending
                        </option>

                        <option value="REVIEWED">
                            Reviewed
                        </option>

                        <option value="DISMISSED">
                            Dismissed
                        </option>

                        <option value="ACTION_TAKEN">
                            Action Taken
                        </option>

                    </select>


                    <select
                        value={type}

                        onChange={
                            (event) =>
                                setType(
                                    event
                                        .target
                                        .value
                                )
                        }

                        style={{
                            padding:
                                '11px 14px',

                            border:
                                '1px solid #d1d5db',

                            borderRadius:
                                '10px',

                            background:
                                '#fff',

                            fontWeight:
                                '600'
                        }}
                    >

                        <option value="">
                            All types
                        </option>

                        <option value="USER">
                            User
                        </option>

                        <option value="MOMENT">
                            Moment
                        </option>

                        <option value="LIVE">
                            Live
                        </option>

                    </select>


                    <button
                        type="button"

                        onClick={
                            applyFilters
                        }

                        style={{
                            border:
                                'none',

                            borderRadius:
                                '10px',

                            padding:
                                '11px 20px',

                            background:
                                '#ff5f91',

                            color:
                                '#fff',

                            fontWeight:
                                '800',

                            cursor:
                                'pointer'
                        }}
                    >
                        Apply
                    </button>


                    <button
                        type="button"

                        onClick={
                            clearFilters
                        }

                        style={{
                            border:
                                '1px solid #d1d5db',

                            borderRadius:
                                '10px',

                            padding:
                                '11px 20px',

                            background:
                                '#fff',

                            color:
                                '#334155',

                            fontWeight:
                                '700',

                            cursor:
                                'pointer'
                        }}
                    >
                        Clear
                    </button>

                </div>


                {/* ERROR */}

                {error && (

                    <div
                        style={{
                            marginBottom:
                                '18px',

                            padding:
                                '14px 16px',

                            borderRadius:
                                '12px',

                            background:
                                '#fef2f2',

                            color:
                                '#b91c1c'
                        }}
                    >
                        {error}
                    </div>

                )}


                {/* REPORTS */}

                {loading ? (

                    <div
                        style={{
                            padding:
                                '40px',

                            textAlign:
                                'center',

                            color:
                                '#64748b'
                        }}
                    >
                        Loading reports...
                    </div>

                ) : reports.length === 0 ? (

                    <div
                        style={{
                            padding:
                                '40px',

                            background:
                                '#fff',

                            borderRadius:
                                '18px',

                            border:
                                '1px solid #e2e8f0',

                            textAlign:
                                'center',

                            color:
                                '#64748b'
                        }}
                    >
                        No reports found.
                    </div>

                ) : (

                    <div
                        style={{
                            display:
                                'grid',

                            gap:
                                '16px'
                        }}
                    >

                        {reports.map(
                            (report) => {

                                const statusStyle =
                                    getStatusStyle(
                                        report.status
                                    );

                                const dropdownOpen =
                                    openActionReportId ===
                                    report.id;


                                return (

                                    <div
                                        key={
                                            report.id
                                        }

                                        style={{
                                            position:
                                                'relative',

                                            background:
                                                '#fff',

                                            padding:
                                                '22px',

                                            borderRadius:
                                                '18px',

                                            border:
                                                '1px solid #e2e8f0',

                                            boxShadow:
                                                '0 4px 14px rgba(15,23,42,.03)'
                                        }}
                                    >

                                        {/* CARD HEADER */}

                                        <div
                                            style={{
                                                display:
                                                    'flex',

                                                justifyContent:
                                                    'space-between',

                                                alignItems:
                                                    'flex-start',

                                                gap:
                                                    '15px',

                                                flexWrap:
                                                    'wrap'
                                            }}
                                        >

                                            <div>

                                                <strong
                                                    style={{
                                                        fontSize:
                                                            '17px',

                                                        color:
                                                            '#0f172a'
                                                    }}
                                                >
                                                    Report #{report.id}
                                                </strong>


                                                <div
                                                    style={{
                                                        marginTop:
                                                            '6px',

                                                        color:
                                                            '#64748b'
                                                    }}
                                                >
                                                    Type: {report.type}
                                                </div>

                                            </div>


                                            <span
                                                style={{
                                                    display:
                                                        'inline-flex',

                                                    alignItems:
                                                        'center',

                                                    padding:
                                                        '7px 12px',

                                                    borderRadius:
                                                        '999px',

                                                    fontSize:
                                                        '12px',

                                                    fontWeight:
                                                        '800',

                                                    ...statusStyle
                                                }}
                                            >
                                                {report.status}
                                            </span>

                                        </div>


                                        {/* DETAILS */}

                                        <div
                                            style={{
                                                display:
                                                    'grid',

                                                gap:
                                                    '8px',

                                                marginTop:
                                                    '18px',

                                                color:
                                                    '#334155'
                                            }}
                                        >

                                            <div>
                                                <strong>
                                                    Reporter:
                                                </strong>{' '}

                                                {report.reporterName ||
                                                    report.reporterUsername ||
                                                    '-'}
                                            </div>


                                            <div>
                                                <strong>
                                                    Reported user:
                                                </strong>{' '}

                                                {report.reportedUserName ||
                                                    report.reportedUserUsername ||
                                                    '-'}
                                            </div>


                                            <div>
                                                <strong>
                                                    Reason:
                                                </strong>{' '}

                                                {report.reason}
                                            </div>


                                            {report.targetId && (

                                                <div>
                                                    <strong>
                                                        Target ID:
                                                    </strong>{' '}

                                                    {report.targetId}
                                                </div>

                                            )}


                                            <div>
                                                <strong>
                                                    Created:
                                                </strong>{' '}

                                                {report.createdAt
                                                    ? new Date(
                                                        report.createdAt
                                                    ).toLocaleString()
                                                    : '-'}
                                            </div>


                                            {report.adminNote && (

                                                <div>
                                                    <strong>
                                                        Admin note:
                                                    </strong>{' '}

                                                    {report.adminNote}
                                                </div>

                                            )}

                                        </div>


                                        {/* CUSTOM DROPDOWN */}

                                        <div
                                            ref={
                                                dropdownOpen
                                                    ? dropdownRef
                                                    : null
                                            }

                                            style={{
                                                position:
                                                    'relative',

                                                display:
                                                    'inline-block',

                                                marginTop:
                                                    '20px'
                                            }}
                                        >

                                            <button
                                                type="button"

                                                disabled={
                                                    actionLoadingId ===
                                                    report.id
                                                }

                                                onClick={() =>
                                                    setOpenActionReportId(
                                                        dropdownOpen
                                                            ? null
                                                            : report.id
                                                    )
                                                }

                                                style={{
                                                    minWidth:
                                                        '160px',

                                                    display:
                                                        'flex',

                                                    justifyContent:
                                                        'space-between',

                                                    alignItems:
                                                        'center',

                                                    gap:
                                                        '20px',

                                                    padding:
                                                        '11px 15px',

                                                    border:
                                                        '1px solid #e2e8f0',

                                                    borderRadius:
                                                        '11px',

                                                    background:
                                                        dropdownOpen
                                                            ? '#fff1f5'
                                                            : '#fff',

                                                    color:
                                                        '#334155',

                                                    fontSize:
                                                        '14px',

                                                    fontWeight:
                                                        '800',

                                                    boxShadow:
                                                        dropdownOpen
                                                            ? '0 0 0 3px rgba(236,72,153,.10)'
                                                            : '0 2px 6px rgba(15,23,42,.04)',

                                                    cursor:
                                                        actionLoadingId ===
                                                            report.id
                                                            ? 'wait'
                                                            : 'pointer'
                                                }}
                                            >

                                                <span>
                                                    {actionLoadingId ===
                                                        report.id
                                                        ? 'Updating...'
                                                        : 'Actions'}
                                                </span>


                                                <span
                                                    style={{
                                                        display:
                                                            'inline-block',

                                                        transform:
                                                            dropdownOpen
                                                                ? 'rotate(180deg)'
                                                                : 'rotate(0)',

                                                        transition:
                                                            'transform .18s ease',

                                                        fontSize:
                                                            '12px'
                                                    }}
                                                >
                                                    ▼
                                                </span>

                                            </button>


                                            {dropdownOpen && (

                                                <div
                                                    style={{
                                                        position:
                                                            'absolute',

                                                        left: 0,

                                                        bottom:
                                                            'calc(100% + 8px)',

                                                        zIndex:
                                                            100,

                                                        width:
                                                            '280px',

                                                        padding:
                                                            '8px',

                                                        border:
                                                            '1px solid #e2e8f0',

                                                        borderRadius:
                                                            '14px',

                                                        background:
                                                            '#fff',

                                                        boxShadow:
                                                            '0 18px 45px rgba(15,23,42,.16)'
                                                    }}
                                                >

                                                    {/* STATUS */}

                                                    <DropdownTitle>
                                                        STATUS
                                                    </DropdownTitle>


                                                    <DropdownItem
                                                        icon="✓"
                                                        title="Mark as Reviewed"
                                                        description="Report has been checked"
                                                        active={
                                                            report.status ===
                                                            'REVIEWED'
                                                        }

                                                        onClick={() =>
                                                            updateReportStatus(
                                                                report.id,
                                                                'review'
                                                            )
                                                        }
                                                    />


                                                    <DropdownItem
                                                        icon="×"
                                                        title="Dismiss Report"
                                                        description="No violation found"
                                                        active={
                                                            report.status ===
                                                            'DISMISSED'
                                                        }

                                                        onClick={() =>
                                                            updateReportStatus(
                                                                report.id,
                                                                'dismiss'
                                                            )
                                                        }
                                                    />


                                                    <DropdownItem
                                                        icon="!"
                                                        title="Take Action"
                                                        description="Apply a moderation action"
                                                        danger
                                                        active={
                                                            report.status === 'ACTION_TAKEN'
                                                        }
                                                        onClick={() =>
                                                            openModerationModal(report)
                                                        }
                                                    />


                                                    {/* COMMUNICATION */}

                                                    <DropdownTitle>
                                                        COMMUNICATION
                                                    </DropdownTitle>


                                                    {report.reportedUserId && (

                                                        <DropdownItem
                                                            icon="✉"
                                                            title="Email User"
                                                            description="Contact the reported user"

                                                            onClick={() =>
                                                                openEmailModal(
                                                                    report,
                                                                    'REPORTED_USER'
                                                                )
                                                            }
                                                        />

                                                    )}


                                                    <DropdownItem
                                                        icon="?"
                                                        title="Request Info"
                                                        description="Ask reporter for more information"

                                                        onClick={() =>
                                                            openEmailModal(
                                                                report,
                                                                'REPORTER'
                                                            )
                                                        }
                                                    />


                                                    <DropdownItem
                                                        icon="◷"
                                                        title="Communication History"
                                                        description="View previous emails"

                                                        onClick={() =>
                                                            openHistoryModal(
                                                                report
                                                            )
                                                        }
                                                    />


                                                    {report.reportedUserId && (
                                                        <>
                                                            <DropdownDivider />

                                                            <DropdownTitle>
                                                                USER
                                                            </DropdownTitle>


                                                            <DropdownItem
                                                                icon="👤"
                                                                title="View User"
                                                                description="Open user moderation profile"

                                                                onClick={() => {
                                                                    setOpenActionReportId(
                                                                        null
                                                                    );

                                                                    window.location.href =
                                                                        `/admin/users/${report.reportedUserId}`;
                                                                }}
                                                            />
                                                        </>
                                                    )}

                                                </div>

                                            )}

                                        </div>

                                    </div>

                                );
                            }
                        )}

                    </div>

                )}


                {/* PAGINATION */}

                {totalPages > 1 && (

                    <div
                        style={{
                            display:
                                'flex',

                            justifyContent:
                                'center',

                            alignItems:
                                'center',

                            gap:
                                '14px',

                            marginTop:
                                '30px'
                        }}
                    >

                        <button
                            type="button"

                            disabled={
                                page === 0
                            }

                            onClick={() =>
                                loadReports(
                                    page - 1,
                                    status,
                                    type
                                )
                            }

                            style={{
                                padding:
                                    '9px 14px',

                                border:
                                    '1px solid #d1d5db',

                                borderRadius:
                                    '9px',

                                background:
                                    '#fff',

                                cursor:
                                    page === 0
                                        ? 'not-allowed'
                                        : 'pointer'
                            }}
                        >
                            Previous
                        </button>


                        <span
                            style={{
                                fontWeight:
                                    '700',

                                color:
                                    '#475569'
                            }}
                        >
                            Page {page + 1} / {totalPages}
                        </span>


                        <button
                            type="button"

                            disabled={
                                page + 1 >=
                                totalPages
                            }

                            onClick={() =>
                                loadReports(
                                    page + 1,
                                    status,
                                    type
                                )
                            }

                            style={{
                                padding:
                                    '9px 14px',

                                border:
                                    '1px solid #d1d5db',

                                borderRadius:
                                    '9px',

                                background:
                                    '#fff',

                                cursor:
                                    page + 1 >=
                                        totalPages
                                        ? 'not-allowed'
                                        : 'pointer'
                            }}
                        >
                            Next
                        </button>

                    </div>

                )}

            </div>


            {/* ==========================
                EMAIL MODAL
            ========================== */}

            {emailModalOpen && emailReport && (

                <div
                    onClick={
                        closeEmailModal
                    }

                    style={{
                        position:
                            'fixed',

                        inset:
                            0,

                        zIndex:
                            9999,

                        display:
                            'flex',

                        alignItems:
                            'center',

                        justifyContent:
                            'center',

                        padding:
                            '20px',

                        background:
                            'rgba(15,23,42,.58)',

                        backdropFilter:
                            'blur(5px)'
                    }}
                >

                    <div
                        onClick={
                            (event) =>
                                event.stopPropagation()
                        }

                        style={{
                            width:
                                '100%',

                            maxWidth:
                                '540px',

                            background:
                                '#fff',

                            borderRadius:
                                '22px',

                            padding:
                                '26px',

                            boxShadow:
                                '0 25px 70px rgba(0,0,0,.25)'
                        }}
                    >

                        <div
                            style={{
                                display:
                                    'flex',

                                justifyContent:
                                    'space-between',

                                alignItems:
                                    'flex-start',

                                gap:
                                    '15px'
                            }}
                        >

                            <div>

                                <h2
                                    style={{
                                        margin:
                                            0,

                                        fontSize:
                                            '23px',

                                        color:
                                            '#0f172a'
                                    }}
                                >
                                    {emailReport.recipientType ===
                                        'REPORTER'
                                        ? 'Request Information'
                                        : 'Email User'}
                                </h2>


                                <p
                                    style={{
                                        margin:
                                            '7px 0 0',

                                        color:
                                            '#64748b',

                                        fontSize:
                                            '14px'
                                    }}
                                >
                                    Message from HalalHeart Safety Team
                                </p>

                            </div>


                            <button
                                type="button"

                                onClick={
                                    closeEmailModal
                                }

                                style={{
                                    width:
                                        '36px',

                                    height:
                                        '36px',

                                    border:
                                        'none',

                                    borderRadius:
                                        '50%',

                                    background:
                                        '#f1f5f9',

                                    fontSize:
                                        '20px',

                                    cursor:
                                        'pointer'
                                }}
                            >
                                ×
                            </button>

                        </div>


                        {/* RECIPIENT */}

                        <div
                            style={{
                                marginTop:
                                    '22px',

                                padding:
                                    '14px 16px',

                                borderRadius:
                                    '12px',

                                background:
                                    '#f8fafc',

                                border:
                                    '1px solid #e2e8f0'
                            }}
                        >

                            <div
                                style={{
                                    fontSize:
                                        '11px',

                                    color:
                                        '#94a3b8',

                                    fontWeight:
                                        '800',

                                    marginBottom:
                                        '5px'
                                }}
                            >
                                TO
                            </div>


                            <div
                                style={{
                                    color:
                                        '#0f172a',

                                    fontWeight:
                                        '800'
                                }}
                            >

                                {emailReport.recipientType ===
                                    'REPORTER'
                                    ? (
                                        emailReport.reporterName ||
                                        emailReport.reporterUsername ||
                                        'Reporter'
                                    )
                                    : (
                                        emailReport.reportedUserName ||
                                        emailReport.reportedUserUsername ||
                                        'User'
                                    )}

                            </div>


                            <div
                                style={{
                                    marginTop:
                                        '3px',

                                    color:
                                        '#64748b',

                                    fontSize:
                                        '14px'
                                }}
                            >

                                {emailReport.recipientType ===
                                    'REPORTER'
                                    ? emailReport.reporterUsername
                                    : emailReport.reportedUserUsername}

                            </div>

                        </div>


                        {/* MESSAGE */}

                        <div
                            style={{
                                marginTop:
                                    '20px'
                            }}
                        >

                            <label
                                style={{
                                    display:
                                        'block',

                                    marginBottom:
                                        '8px',

                                    color:
                                        '#334155',

                                    fontWeight:
                                        '800',

                                    fontSize:
                                        '14px'
                                }}
                            >
                                Message
                            </label>


                            <textarea
                                value={
                                    emailBody
                                }

                                onChange={
                                    (event) =>
                                        setEmailBody(
                                            event.target.value
                                        )
                                }

                                placeholder={
                                    emailReport.recipientType ===
                                        'REPORTER'
                                        ? 'Write your request for additional information...'
                                        : 'Write your message to the user...'
                                }

                                rows={8}

                                autoFocus

                                style={{
                                    width:
                                        '100%',

                                    boxSizing:
                                        'border-box',

                                    padding:
                                        '15px',

                                    border:
                                        '1px solid #cbd5e1',

                                    borderRadius:
                                        '13px',

                                    outline:
                                        'none',

                                    resize:
                                        'vertical',

                                    fontFamily:
                                        'inherit',

                                    fontSize:
                                        '15px',

                                    lineHeight:
                                        '1.6'
                                }}
                            />

                        </div>


                        {emailSuccess && (

                            <div
                                style={{
                                    marginTop:
                                        '13px',

                                    padding:
                                        '11px',

                                    borderRadius:
                                        '10px',

                                    background:
                                        '#ecfdf5',

                                    color:
                                        '#15803d',

                                    fontWeight:
                                        '700'
                                }}
                            >
                                {emailSuccess}
                            </div>

                        )}


                        <div
                            style={{
                                display:
                                    'flex',

                                justifyContent:
                                    'flex-end',

                                gap:
                                    '10px',

                                marginTop:
                                    '20px'
                            }}
                        >

                            <button
                                type="button"

                                onClick={
                                    closeEmailModal
                                }

                                disabled={
                                    emailSending
                                }

                                style={{
                                    padding:
                                        '11px 18px',

                                    border:
                                        '1px solid #cbd5e1',

                                    borderRadius:
                                        '10px',

                                    background:
                                        '#fff',

                                    fontWeight:
                                        '700',

                                    cursor:
                                        'pointer'
                                }}
                            >
                                Cancel
                            </button>


                            <button
                                type="button"

                                onClick={
                                    sendReportEmail
                                }

                                disabled={
                                    emailSending ||
                                    !emailBody.trim()
                                }

                                style={{
                                    padding:
                                        '11px 20px',

                                    border:
                                        'none',

                                    borderRadius:
                                        '10px',

                                    background:
                                        emailSending ||
                                            !emailBody.trim()
                                            ? '#f9a8d4'
                                            : '#ec4899',

                                    color:
                                        '#fff',

                                    fontWeight:
                                        '800',

                                    cursor:
                                        emailSending ||
                                            !emailBody.trim()
                                            ? 'not-allowed'
                                            : 'pointer'
                                }}
                            >

                                {emailSending
                                    ? 'Sending...'
                                    : 'Send Email'}

                            </button>

                        </div>

                    </div>

                </div>

            )}


            {/* ==========================
                HISTORY MODAL
            ========================== */}

            {historyModalOpen &&
                historyReport && (

                    <div
                        onClick={
                            closeHistoryModal
                        }

                        style={{
                            position:
                                'fixed',

                            inset:
                                0,

                            zIndex:
                                9999,

                            display:
                                'flex',

                            alignItems:
                                'center',

                            justifyContent:
                                'center',

                            padding:
                                '20px',

                            background:
                                'rgba(15,23,42,.58)',

                            backdropFilter:
                                'blur(5px)'
                        }}
                    >

                        <div
                            onClick={
                                (event) =>
                                    event.stopPropagation()
                            }

                            style={{
                                width:
                                    '100%',

                                maxWidth:
                                    '620px',

                                maxHeight:
                                    '85vh',

                                overflowY:
                                    'auto',

                                background:
                                    '#fff',

                                borderRadius:
                                    '22px',

                                padding:
                                    '26px',

                                boxShadow:
                                    '0 25px 70px rgba(0,0,0,.25)'
                            }}
                        >

                            <div
                                style={{
                                    display:
                                        'flex',

                                    justifyContent:
                                        'space-between',

                                    alignItems:
                                        'flex-start',

                                    gap:
                                        '15px'
                                }}
                            >

                                <div>

                                    <h2
                                        style={{
                                            margin:
                                                0,

                                            fontSize:
                                                '23px',

                                            color:
                                                '#0f172a'
                                        }}
                                    >
                                        Communication History
                                    </h2>


                                    <p
                                        style={{
                                            margin:
                                                '6px 0 0',

                                            color:
                                                '#64748b'
                                        }}
                                    >
                                        Report #{historyReport.id}
                                    </p>

                                </div>


                                <button
                                    type="button"

                                    onClick={
                                        closeHistoryModal
                                    }

                                    style={{
                                        width:
                                            '36px',

                                        height:
                                            '36px',

                                        border:
                                            'none',

                                        borderRadius:
                                            '50%',

                                        background:
                                            '#f1f5f9',

                                        fontSize:
                                            '20px',

                                        cursor:
                                            'pointer'
                                    }}
                                >
                                    ×
                                </button>

                            </div>


                            <div
                                style={{
                                    marginTop:
                                        '22px'
                                }}
                            >

                                {historyLoading ? (

                                    <div
                                        style={{
                                            padding:
                                                '25px',

                                            textAlign:
                                                'center',

                                            color:
                                                '#64748b'
                                        }}
                                    >
                                        Loading history...
                                    </div>

                                ) : historyItems.length ===
                                    0 ? (

                                    <div
                                        style={{
                                            padding:
                                                '25px',

                                            textAlign:
                                                'center',

                                            background:
                                                '#f8fafc',

                                            borderRadius:
                                                '12px',

                                            color:
                                                '#64748b'
                                        }}
                                    >
                                        No communication history yet.
                                    </div>

                                ) : (

                                    <div
                                        style={{
                                            display:
                                                'grid',

                                            gap:
                                                '14px'
                                        }}
                                    >

                                        {historyItems.map(
                                            (item) => (

                                                <div
                                                    key={
                                                        item.id
                                                    }

                                                    style={{
                                                        padding:
                                                            '16px',

                                                        border:
                                                            '1px solid #e2e8f0',

                                                        borderRadius:
                                                            '14px',

                                                        background:
                                                            '#f8fafc'
                                                    }}
                                                >

                                                    <div
                                                        style={{
                                                            display:
                                                                'flex',

                                                            justifyContent:
                                                                'space-between',

                                                            gap:
                                                                '12px',

                                                            flexWrap:
                                                                'wrap'
                                                        }}
                                                    >

                                                        <strong
                                                            style={{
                                                                color:
                                                                    item.recipientType ===
                                                                        'REPORTER'
                                                                        ? '#0369a1'
                                                                        : '#be185d'
                                                            }}
                                                        >

                                                            {item.recipientType ===
                                                                'REPORTER'
                                                                ? 'Request Info'
                                                                : 'Email User'}

                                                        </strong>


                                                        <span
                                                            style={{
                                                                color:
                                                                    '#64748b',

                                                                fontSize:
                                                                    '13px'
                                                            }}
                                                        >

                                                            {item.createdAt
                                                                ? new Date(
                                                                    item.createdAt
                                                                ).toLocaleString()
                                                                : ''}

                                                        </span>

                                                    </div>


                                                    <div
                                                        style={{
                                                            marginTop:
                                                                '8px',

                                                            fontSize:
                                                                '14px'
                                                        }}
                                                    >
                                                        <strong>
                                                            To:
                                                        </strong>{' '}

                                                        {item.recipientEmail}
                                                    </div>


                                                    <div
                                                        style={{
                                                            marginTop:
                                                                '12px',

                                                            padding:
                                                                '12px',

                                                            background:
                                                                '#fff',

                                                            borderRadius:
                                                                '10px',

                                                            whiteSpace:
                                                                'pre-wrap',

                                                            lineHeight:
                                                                '1.6',

                                                            color:
                                                                '#334155'
                                                        }}
                                                    >
                                                        {item.message}
                                                    </div>

                                                </div>

                                            )
                                        )}

                                    </div>

                                )}

                            </div>

                        </div>

                    </div>

                )}

            {actionModalOpen && actionReport && (

                <div
                    onClick={closeActionModal}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 9999,

                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',

                        padding: '20px',

                        background:
                            'rgba(15,23,42,.58)',

                        backdropFilter:
                            'blur(5px)'
                    }}
                >

                    <div
                        onClick={(event) =>
                            event.stopPropagation()
                        }
                        style={{
                            width: '100%',
                            maxWidth: '520px',

                            padding: '26px',

                            background: '#fff',

                            borderRadius: '22px',

                            boxShadow:
                                '0 25px 70px rgba(0,0,0,.25)'
                        }}
                    >

                        <h2
                            style={{
                                margin: 0,
                                color: '#0f172a'
                            }}
                        >
                            Take Moderation Action
                        </h2>


                        <p
                            style={{
                                marginTop: '7px',
                                color: '#64748b'
                            }}
                        >
                            Report #{actionReport.id}
                        </p>


                        <div
                            style={{
                                display: 'grid',
                                gap: '10px',
                                marginTop: '22px'
                            }}
                        >

                            {[
                                'WARNING_SENT',
                                'USER_SUSPENDED',
                                'USER_BANNED',
                                'CONTENT_REMOVED',
                                'OTHER'
                            ].map((action) => (

                                <label
                                    key={action}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',

                                        padding: '13px',

                                        border:
                                            moderationAction === action
                                                ? '2px solid #ec4899'
                                                : '1px solid #e2e8f0',

                                        borderRadius: '12px',

                                        cursor: 'pointer',

                                        background:
                                            moderationAction === action
                                                ? '#fdf2f8'
                                                : '#fff'
                                    }}
                                >

                                    <input
                                        type="radio"

                                        name="moderationAction"

                                        value={action}

                                        checked={
                                            moderationAction ===
                                            action
                                        }

                                        onChange={() =>
                                            setModerationAction(
                                                action
                                            )
                                        }
                                    />


                                    <span
                                        style={{
                                            fontWeight: '700'
                                        }}
                                    >

                                        {action === 'WARNING_SENT'
                                            ? 'Warning sent'
                                            : action === 'USER_SUSPENDED'
                                                ? 'User suspended'
                                                : action === 'USER_BANNED'
                                                    ? 'User banned'
                                                    : action === 'CONTENT_REMOVED'
                                                        ? 'Content removed'
                                                        : 'Other'}

                                    </span>

                                </label>

                            ))}

                        </div>


                        <textarea
                            value={actionNote}

                            onChange={(event) =>
                                setActionNote(
                                    event.target.value
                                )
                            }

                            placeholder="Optional admin note..."

                            rows={4}

                            style={{
                                width: '100%',
                                boxSizing: 'border-box',

                                marginTop: '18px',

                                padding: '13px',

                                border:
                                    '1px solid #cbd5e1',

                                borderRadius: '12px',

                                resize: 'vertical',

                                fontFamily: 'inherit'
                            }}
                        />


                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '10px',

                                marginTop: '20px'
                            }}
                        >

                            <button
                                type="button"
                                onClick={closeActionModal}
                            >
                                Cancel
                            </button>


                            <button
                                type="button"

                                disabled={
                                    !moderationAction ||
                                    actionLoadingId ===
                                    actionReport.id
                                }

                                onClick={
                                    confirmModerationAction
                                }

                                style={{
                                    border: 'none',
                                    borderRadius: '10px',

                                    padding: '11px 18px',

                                    background:
                                        !moderationAction
                                            ? '#fda4af'
                                            : '#ef4444',

                                    color: '#fff',

                                    fontWeight: '800',

                                    cursor:
                                        !moderationAction
                                            ? 'not-allowed'
                                            : 'pointer'
                                }}
                            >

                                {actionLoadingId ===
                                    actionReport.id
                                    ? 'Saving...'
                                    : 'Confirm Action'}

                            </button>

                        </div>

                    </div>

                </div>

            )}

            {moderationModalOpen && moderationReport && (

                <div
                    onClick={closeModerationModal}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 9999,

                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',

                        padding: '20px',

                        background:
                            'rgba(15,23,42,.58)',

                        backdropFilter:
                            'blur(5px)'
                    }}
                >

                    <div
                        onClick={(event) =>
                            event.stopPropagation()
                        }
                        style={{
                            width: '100%',
                            maxWidth: '520px',

                            padding: '26px',

                            borderRadius: '22px',

                            background: '#fff',

                            boxShadow:
                                '0 25px 70px rgba(0,0,0,.25)'
                        }}
                    >

                        <h2
                            style={{
                                margin: 0,
                                color: '#0f172a'
                            }}
                        >
                            Take Action
                        </h2>

                        <p
                            style={{
                                marginTop: '7px',
                                color: '#64748b'
                            }}
                        >
                            Report #{moderationReport.id}
                        </p>

                        <div
                            style={{
                                display: 'grid',
                                gap: '10px',
                                marginTop: '20px'
                            }}
                        >

                            {[
                                {
                                    value: 'WARNING',
                                    label: 'Send Warning'
                                },
                                {
                                    value: 'SUSPEND',
                                    label: 'Suspend User'
                                },
                                {
                                    value: 'BAN',
                                    label: 'Ban User'
                                },
                                {
                                    value: 'CONTENT_REMOVED',
                                    label: 'Content Removed'
                                },
                                {
                                    value: 'OTHER',
                                    label: 'Other'
                                }
                            ].map((item) => (

                                <button
                                    key={item.value}
                                    type="button"

                                    onClick={() =>
                                        setModerationAction(
                                            item.value
                                        )
                                    }

                                    style={{
                                        width: '100%',

                                        padding: '13px 15px',

                                        borderRadius: '12px',

                                        border:
                                            moderationAction ===
                                                item.value
                                                ? '2px solid #ec4899'
                                                : '1px solid #e2e8f0',

                                        background:
                                            moderationAction ===
                                                item.value
                                                ? '#fdf2f8'
                                                : '#fff',

                                        color:
                                            '#334155',

                                        textAlign:
                                            'left',

                                        fontWeight:
                                            '800',

                                        cursor:
                                            'pointer'
                                    }}
                                >
                                    {item.label}
                                </button>

                            ))}

                        </div>

                        <textarea
                            value={moderationNote}

                            onChange={(event) =>
                                setModerationNote(
                                    event.target.value
                                )
                            }

                            placeholder="Optional admin note..."

                            rows={4}

                            style={{
                                width: '100%',
                                boxSizing: 'border-box',

                                marginTop: '18px',

                                padding: '13px',

                                border:
                                    '1px solid #cbd5e1',

                                borderRadius: '12px',

                                resize: 'vertical',

                                fontFamily: 'inherit'
                            }}
                        />

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '10px',
                                marginTop: '20px'
                            }}
                        >

                            <button
                                type="button"
                                onClick={closeModerationModal}
                                style={{
                                    padding: '11px 18px',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '10px',
                                    background: '#fff',
                                    fontWeight: '700',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"

                                disabled={
                                    !moderationAction
                                }

                                onClick={
                                    confirmModerationAction
                                }

                                style={{
                                    padding: '11px 18px',

                                    border: 'none',

                                    borderRadius: '10px',

                                    background:
                                        moderationAction
                                            ? '#ef4444'
                                            : '#fca5a5',

                                    color: '#fff',

                                    fontWeight: '800',

                                    cursor:
                                        moderationAction
                                            ? 'pointer'
                                            : 'not-allowed'
                                }}
                            >
                                Confirm Action
                            </button>

                        </div>

                    </div>

                </div>

            )}

        </main>
    );
};


/*
 * =====================================
 * CUSTOM DROPDOWN SMALL COMPONENTS
 * =====================================
 */

const DropdownTitle = ({
    children
}) => (

    <div
        style={{
            padding:
                '7px 10px 5px',

            fontSize:
                '10px',

            fontWeight:
                '900',

            letterSpacing:
                '.08em',

            color:
                '#94a3b8'
        }}
    >
        {children}
    </div>
);


const DropdownDivider = () => (

    <div
        style={{
            height:
                '1px',

            margin:
                '7px 4px',

            background:
                '#e2e8f0'
        }}
    />

);


const DropdownItem = ({
    icon,
    title,
    description,
    onClick,
    active = false,
    danger = false
}) => (

    <button
        type="button"

        onClick={
            onClick
        }

        style={{
            width:
                '100%',

            display:
                'flex',

            alignItems:
                'center',

            gap:
                '11px',

            padding:
                '10px',

            border:
                'none',

            borderRadius:
                '10px',

            background:
                active
                    ? (
                        danger
                            ? '#fef2f2'
                            : '#f1f5f9'
                    )
                    : 'transparent',

            color:
                danger
                    ? '#b91c1c'
                    : '#334155',

            textAlign:
                'left',

            cursor:
                'pointer'
        }}

        onMouseEnter={
            (event) => {

                if (!active) {
                    event.currentTarget
                        .style
                        .background =
                        '#f8fafc';
                }
            }
        }

        onMouseLeave={
            (event) => {

                if (!active) {
                    event.currentTarget
                        .style
                        .background =
                        'transparent';
                }
            }
        }
    >

        <span
            style={{
                width:
                    '34px',

                height:
                    '34px',

                flex:
                    '0 0 34px',

                display:
                    'inline-flex',

                alignItems:
                    'center',

                justifyContent:
                    'center',

                borderRadius:
                    '9px',

                background:
                    danger
                        ? '#fee2e2'
                        : active
                            ? '#e0e7ff'
                            : '#f1f5f9',

                color:
                    danger
                        ? '#b91c1c'
                        : '#475569',

                fontWeight:
                    '900'
            }}
        >
            {icon}
        </span>


        <span
            style={{
                minWidth:
                    0,

                flex:
                    1
            }}
        >

            <span
                style={{
                    display:
                        'block',

                    fontSize:
                        '14px',

                    fontWeight:
                        '800'
                }}
            >
                {title}

                {active && (
                    <span
                        style={{
                            marginLeft:
                                '7px',

                            color:
                                '#16a34a',

                            fontSize:
                                '11px'
                        }}
                    >
                        CURRENT
                    </span>
                )}

            </span>


            <span
                style={{
                    display:
                        'block',

                    marginTop:
                        '2px',

                    color:
                        '#94a3b8',

                    fontSize:
                        '11px',

                    lineHeight:
                        '1.35'
                }}
            >
                {description}
            </span>

        </span>

    </button>
);


export default AdminReports;